import {
  listBackupRestores,
  listBackups,
  listBackupSchedules,
} from '@qdrant/qdrant-cloud-public-api/serverless/space/backup/v1/connect-query';
import { createFileRoute, lazyRouteComponent } from '@tanstack/react-router';
import { spaceService } from '@/api/services/serverless/space';
import { spaceBackupService } from '@/api/services/serverless/space-backup';
import { resolveAccountHasRpcPermissions } from '@/router/resolvers/user';

export const Route = createFileRoute(
  '/_authenticated/_dashboard/accounts/$accountId/serverless-spaces/$spaceId/_tabs/backups',
)({
  // The backups feature has its own owner, so tag this subtree with the `backups` domain.
  staticData: {
    domain: 'backups',
  },
  beforeLoad: ({ params: { accountId, spaceId } }) => ({
    listBackupsQuery: spaceBackupService.listBackupsQuery({ accountId, spaceId }),
    listBackupSchedulesQuery: spaceBackupService.listBackupSchedulesQuery({ accountId, spaceId }),
    listBackupRestoresQuery: spaceBackupService.listBackupRestoresQuery({ accountId, spaceId }),
    // Per row, for the restore and create-from-backup dialogs.
    getBackupQuery: (backupId: string) => spaceBackupService.getBackupQuery({ accountId, backupId }),
    suggestSpaceNameQuery: spaceService.suggestSpaceNameQuery({ accountId }),
  }),
  // Each section is gated on its own permission: prefetch only what the account may read, so a refusal
  // neither denies the whole tab nor reaches Sentry. Not awaited: the sections show skeletons meanwhile.
  loader: async ({ context, params: { accountId } }) => {
    const { queryClient } = context;
    // Public: `SuggestSpaceName` declares no permission.
    void queryClient.prefetchQuery(context.suggestSpaceNameQuery);

    // Thunks rather than the options: one `prefetchQuery` call cannot take a union of three response types.
    const sections = [
      [listBackups, () => queryClient.prefetchQuery(context.listBackupsQuery)],
      [listBackupSchedules, () => queryClient.prefetchQuery(context.listBackupSchedulesQuery)],
      [listBackupRestores, () => queryClient.prefetchQuery(context.listBackupRestoresQuery)],
    ] as const;

    await Promise.all(
      sections.map(async ([method, prefetch]) => {
        if (await resolveAccountHasRpcPermissions([method], { queryClient, accountId })) {
          void prefetch();
        }
      }),
    );
  },
  component: lazyRouteComponent(() => import('@/components/Serverless/SpaceBackups'), 'SpaceBackups'),
});
